import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { 
  MessageSquare, Send, Paperclip, Clock, CheckCircle, 
  AlertCircle, ChevronLeft, Plus, User, Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

const statusColors = {
  open: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  resolved: 'bg-green-500/20 text-green-400 border-green-500/30',
  closed: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
};

const statusIcons = {
  open: <AlertCircle className="w-3 h-3" />,
  pending: <Clock className="w-3 h-3" />,
  resolved: <CheckCircle className="w-3 h-3" />,
  closed: <CheckCircle className="w-3 h-3" />
};

export default function HRSupportChat() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const messagesEndRef = useRef(null);

  const user = JSON.parse(localStorage.getItem('hrms_user') || '{}');

  useEffect(() => {
    fetchConversations();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [selectedConversation?.messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API}/hr-support/conversations`);
      setConversations(response.data.conversations || []);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const startNewConversation = async () => {
    if (!newMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API}/hr-support/conversations`, {
        message: newMessage
      });
      
      toast.success('Message sent to HR');
      setNewMessage('');
      setShowNewChat(false);
      await fetchConversations();
      setSelectedConversation(response.data.conversation);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    setSending(true);
    try {
      await axios.post(
        `${API}/hr-support/conversations/${selectedConversation.conversation_id}/messages`,
        { message: newMessage }
      );
      
      // Refresh conversation
      const response = await axios.get(
        `${API}/hr-support/conversations/${selectedConversation.conversation_id}`
      );
      setSelectedConversation(response.data.conversation);
      setNewMessage('');
      await fetchConversations();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const selectConversation = async (conv) => {
    setSelectedConversation(conv);
    setShowNewChat(false);
    
    // Mark as read
    if (conv.unread_by_employee) {
      await axios.put(`${API}/hr-support/conversations/${conv.conversation_id}/read`);
      fetchConversations();
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="h-[calc(100vh-120px)] flex bg-[#0a1628] rounded-xl border border-white/10 overflow-hidden">
      {/* Conversations List */}
      <div className={`w-80 border-r border-white/10 flex flex-col ${selectedConversation ? 'hidden md:flex' : 'flex'}`}>
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-emerald-400" />
              HR Support
            </h2>
            <Button
              size="sm"
              onClick={() => { setShowNewChat(true); setSelectedConversation(null); }}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-xs text-gray-400">Chat directly with HR team</p>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-gray-400">Loading...</div>
          ) : conversations.length === 0 ? (
            <div className="p-4 text-center text-gray-400">
              <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No conversations yet</p>
              <p className="text-xs mt-1">Start a new chat with HR</p>
            </div>
          ) : (
            conversations.map((conv) => (
              <div
                key={conv.conversation_id}
                onClick={() => selectConversation(conv)}
                className={`p-3 border-b border-white/5 cursor-pointer hover:bg-white/5 transition-colors ${
                  selectedConversation?.conversation_id === conv.conversation_id ? 'bg-white/10' : ''
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate font-medium">
                      {conv.subject}
                    </p>
                    <p className="text-xs text-gray-400 truncate mt-1">
                      {conv.messages?.[conv.messages.length - 1]?.message || 'No messages'}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-xs text-gray-500">
                      {formatTime(conv.updated_at)}
                    </span>
                    {conv.unread_by_employee && (
                      <span className="w-2 h-2 bg-emerald-400 rounded-full" />
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className={`text-xs ${statusColors[conv.status]}`}>
                    {statusIcons[conv.status]}
                    <span className="ml-1 capitalize">{conv.status}</span>
                  </Badge>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className={`flex-1 flex flex-col ${!selectedConversation && !showNewChat ? 'hidden md:flex' : 'flex'}`}>
        {showNewChat ? (
          /* New Chat */
          <div className="flex-1 flex flex-col">
            <div className="p-4 border-b border-white/10 flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowNewChat(false)}
                className="md:hidden"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div>
                <h3 className="text-white font-medium">New Message to HR</h3>
                <p className="text-xs text-gray-400">Start a conversation with HR team</p>
              </div>
            </div>
            
            <div className="flex-1 flex items-center justify-center p-4">
              <div className="text-center max-w-md">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 text-emerald-400/50" />
                <h3 className="text-xl text-white mb-2">Contact HR</h3>
                <p className="text-gray-400 text-sm mb-6">
                  Have a question or need assistance? Send a message to the HR team and they'll get back to you.
                </p>
              </div>
            </div>
            
            <div className="p-4 border-t border-white/10">
              <div className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your message to HR..."
                  className="flex-1 bg-white/5 border-white/10 text-white"
                  onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && startNewConversation()}
                />
                <Button 
                  onClick={startNewConversation}
                  disabled={sending || !newMessage.trim()}
                  className="bg-emerald-500 hover:bg-emerald-600"
                >
                  {sending ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        ) : selectedConversation ? (
          /* Selected Conversation */
          <>
            <div className="p-4 border-b border-white/10 flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedConversation(null)}
                className="md:hidden"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div className="flex-1">
                <h3 className="text-white font-medium truncate">{selectedConversation.subject}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={`text-xs ${statusColors[selectedConversation.status]}`}>
                    {statusIcons[selectedConversation.status]}
                    <span className="ml-1 capitalize">{selectedConversation.status}</span>
                  </Badge>
                  <span className="text-xs text-gray-400">
                    Started {formatTime(selectedConversation.created_at)}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {selectedConversation.messages?.map((msg) => (
                <div
                  key={msg.message_id}
                  className={`flex ${msg.sender_type === 'employee' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                      msg.sender_type === 'employee'
                        ? 'bg-emerald-500 text-white rounded-br-md'
                        : 'bg-white/10 text-white rounded-bl-md'
                    }`}
                  >
                    {msg.sender_type === 'hr' && (
                      <p className="text-xs text-emerald-400 font-medium mb-1">
                        {msg.sender_name} (HR)
                      </p>
                    )}
                    <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                    <p className={`text-xs mt-1 ${
                      msg.sender_type === 'employee' ? 'text-white/70' : 'text-gray-400'
                    }`}>
                      {formatTime(msg.timestamp)}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
            
            {selectedConversation.status !== 'closed' && (
              <div className="p-4 border-t border-white/10">
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 bg-white/5 border-white/10 text-white"
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                  />
                  <Button 
                    onClick={sendMessage}
                    disabled={sending || !newMessage.trim()}
                    className="bg-emerald-500 hover:bg-emerald-600"
                  >
                    {sending ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          /* No Selection */
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-600" />
              <h3 className="text-xl text-white mb-2">HR Support Chat</h3>
              <p className="text-gray-400 mb-4">Select a conversation or start a new one</p>
              <Button
                onClick={() => setShowNewChat(true)}
                className="bg-emerald-500 hover:bg-emerald-600"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Message
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
